/**
 * apiClient — PrintBox3D
 *
 * Centralised fetch wrapper that handles:
 *  - JWT Authorization header injection
 *  - Automatic access-token refresh on 401
 *  - Consistent JSON error parsing
 *  - Base URL from environment variable
 *
 * Usage:
 *   import apiClient from './apiClient';
 *   const data = await apiClient.get('/api/products/');
 *   const result = await apiClient.post('/api/orders/create/', payload);
 */

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// ---------------------------------------------------------------------------
// Token helpers (localStorage with sessionStorage fallback)
// ---------------------------------------------------------------------------
const getToken = () =>
  localStorage.getItem('authToken') || sessionStorage.getItem('authToken');

const getRefreshToken = () =>
  localStorage.getItem('refreshToken') || sessionStorage.getItem('refreshToken');

const saveTokens = (access, refresh, user = null) => {
  localStorage.setItem('authToken', access);
  if (refresh) localStorage.setItem('refreshToken', refresh);
  sessionStorage.setItem('authToken', access);
  if (user) {
    localStorage.setItem('user', JSON.stringify(user));
    sessionStorage.setItem('user', JSON.stringify(user));
  }
};

const clearTokens = () => {
  ['authToken', 'refreshToken', 'user'].forEach((k) => {
    localStorage.removeItem(k);
    sessionStorage.removeItem(k);
  });
};

// ---------------------------------------------------------------------------
// Token refresh
// ---------------------------------------------------------------------------
let _refreshPromise = null; // deduplicate concurrent refresh attempts

async function refreshAccessToken() {
  if (_refreshPromise) return _refreshPromise;

  _refreshPromise = (async () => {
    const refreshToken = getRefreshToken();
    if (!refreshToken) throw new Error('No refresh token available');

    const response = await fetch(`${BASE_URL}/api/auth/token/refresh/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh: refreshToken }),
    });

    if (!response.ok) {
      clearTokens();
      throw new Error('Session expired. Please log in again.');
    }

    const data = await response.json();
    saveTokens(data.access, data.refresh);
    return data.access;
  })().finally(() => {
    _refreshPromise = null;
  });

  return _refreshPromise;
}

// ---------------------------------------------------------------------------
// Core request function
// ---------------------------------------------------------------------------
/**
 * Make an authenticated HTTP request to the backend.
 *
 * @param {string} path        - API path, e.g. '/api/products/'
 * @param {RequestInit} options - fetch options (method, body, headers …)
 * @param {boolean} retry      - internal flag to prevent infinite retry loops
 * @returns {Promise<any>}       Parsed JSON response
 */
async function request(path, options = {}, retry = true) {
  const token = getToken();

  const headers = {
    ...(!options._isFormData && { 'Content-Type': 'application/json' }),
    ...(token && { Authorization: `Bearer ${token}` }),
    // Prevent mobile browsers from serving a cached empty/error response
    ...((!options.method || options.method === 'GET') && {
      'Cache-Control': 'no-cache',
    }),
    ...options.headers,
  };

  // Prevent Content-Type from being set on FormData (browser sets it with boundary)
  if (options._isFormData) delete headers['Content-Type'];

  const response = await fetch(`${BASE_URL}${path}`, {
    ...options,
    headers,
  });

  // -- Token expired → attempt refresh and retry once -----------------------
  if (response.status === 401 && retry) {
    try {
      await refreshAccessToken();
      return request(path, options, false); // retry with new token
    } catch {
      clearTokens();
      // Redirect to login if running in a browser context
      if (typeof window !== 'undefined') window.location.href = '/login';
      throw new Error('Session expired. Please log in again.');
    }
  }

  // -- Parse body -----------------------------------------------------------
  const contentType = response.headers.get('content-type') || '';
  const body = contentType.includes('application/json')
    ? await response.json()
    : await response.text();

  // -- Error handling --------------------------------------------------------
  if (!response.ok) {
    const message =
      (typeof body === 'object' && (body.detail || body.error || body.message)) ||
      (typeof body === 'string' && body) ||
      `HTTP ${response.status}`;

    const error = new Error(message);
    error.status = response.status;
    error.data = body;
    throw error;
  }

  return body;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------
const apiClient = {
  /** GET request */
  get: (path, options = {}) => request(path, { ...options, method: 'GET' }),

  /** POST request with JSON body */
  post: (path, data, options = {}) =>
    request(path, {
      ...options,
      method: 'POST',
      body: JSON.stringify(data),
    }),

  /** PUT request with JSON body */
  put: (path, data, options = {}) =>
    request(path, {
      ...options,
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  /** PATCH request with JSON body */
  patch: (path, data, options = {}) =>
    request(path, {
      ...options,
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  /** DELETE request */
  delete: (path, options = {}) => request(path, { ...options, method: 'DELETE' }),

  /**
   * POST with FormData (file uploads).
   * Do NOT set Content-Type — browser adds multipart boundary automatically.
   */
  upload: (path, formData, options = {}) =>
    request(path, {
      ...options,
      method: 'POST',
      body: formData,
      _isFormData: true,
    }),

  /**
   * Upload a file directly to S3 using a presigned PUT URL.
   * The presigned URL is obtained from the backend first.
   *
   * Workflow:
   *  1. Call  POST /api/s3/presigned-upload/  → { upload_url, file_url, s3_key }
   *  2. PUT the file bytes directly to upload_url (no auth header needed)
   *  3. Store file_url in the form/state and submit to backend
   *
   * @param {string} folder       - S3 folder ('products', 'custom_orders' …)
   * @param {File} file           - Browser File object
   * @returns {Promise<string>}     Public S3 URL of the uploaded file
   */
  uploadToS3: async (folder, file) => {
    // 1. Request presigned URL from backend
    const { upload_url, file_url } = await apiClient.post('/api/s3/presigned-upload/', {
      folder,
      file_name: file.name,
      content_type: file.type,
      file_size: file.size,
    });

    // 2. Upload file directly to S3 (no auth header — presigned URL already has credentials)
    const s3Response = await fetch(upload_url, {
      method: 'PUT',
      headers: { 'Content-Type': file.type },
      body: file,
    });

    if (!s3Response.ok) {
      throw new Error(`S3 upload failed: ${s3Response.status}`);
    }

    // 3. Return the public URL for storage
    return file_url;
  },

  // Expose helpers for contexts that need to manage tokens
  saveTokens,
  clearTokens,
  getToken,
};

export default apiClient;
